<template>
	<div class="inline-block">

		<div v-if="showChat" ref="chatPanel" class="fixed inset-x-2 top-16 bottom-2 z-[35] card bg-base shadow-xl flex flex-col overflow-hidden md:absolute md:inset-x-auto md:right-4 md:top-full md:bottom-auto md:mt-1 md:w-[min(90vw,30rem)] md:h-[min(80vh,44rem)]">
			<div class="relative h-full">
				<div class="absolute inset-x-0 top-0 z-10 backdrop-blur-sm flex items-center justify-end gap-2 px-3 py-2">
					<div class="tooltip tooltip-left" :data-tip="$t('Reset')">
						<button type="button" class="btn btn-ghost border-none btn-xs btn-circle" :disabled="loading" @click="restartChat">
							<ArrowPathIcon class="w-4 h-4" />
						</button>
					</div>
					<button type="button" class="btn btn-ghost border-none btn-xs btn-circle" :title="$t('Close')" @click="showChat = false">
						<XMarkIcon class="w-4 h-4" />
					</button>
				</div>

				<div ref="messageList" class="chat-scroll h-full overflow-y-auto overflow-x-hidden px-4 pt-10 pb-36 space-y-2" @click="handleMessageLinkClick">
					<div v-for="(m, i) in messages" :key="i" :class="['chat', m.role === 'user' ? 'chat-end ml-10' : 'chat-start mr-10', 'chat-message-in']">
						<div class="chat-image avatar">
							<UserAvatar v-if="m.role === 'user' && me" :user="me" class="w-12 h-12" />
							<div v-else class="w-12 rounded-full">
								<img src="/images/SmartSymbols-Tibo.png" alt="Tibo">
							</div>
						</div>
						<div class="chat-header text-xs opacity-60 mb-1">
							{{ m.role === 'user' ? $t('You') : 'Tibo' }} - <time>{{ m.at }}</time>
						</div>
						<div :class="['chat-bubble agent-markdown break-words', m.error ? 'chat-bubble-error' : m.role === 'user' ? 'bg-indigo-700 text-base' : 'bg-indigo-100 text-base-content']" v-html="renderMarkdown(m.content)"></div>
					</div>

					<div v-if="loading || greeting" class="chat chat-start">
						<div class="chat-image avatar">
							<div class="w-12 rounded-full">
								<img src="/images/SmartSymbols-Tibo.png" alt="Tibo">
							</div>
						</div>
						<div class="chat-bubble bg-indigo-100 text-base-content mt-4">
							<span class="loading loading-dots loading-sm"></span>
						</div>
					</div>
				</div>

				<div class="absolute inset-x-0 bottom-0 z-10 flex flex-col backdrop-blur-sm px-3 pt-2">
					<form @submit.prevent="sendMessage" class="flex items-start gap-2">
						<textarea ref="input" v-model="input" :disabled="loading" :placeholder="$t('Ask your question here')" maxlength="2000" class="textarea textarea-bordered flex-1 leading-5 h-20 resize-none" @keydown.enter="handleEnterKey" />
						<button type="submit" class="btn btn-primary btn-sm" :disabled="loading || !input.trim()" aria-label="Send message">
							<PaperAirplaneIcon class="w-5 h-5" />
						</button>
					</form>
					<div class="text-xs text-center text-base-content-light my-4">
						{{ $t("Tibo’s AI generated answers may occasionally be inaccurate.") }}
					</div>
				</div>
			</div>
		</div>
	</div>

	<KBView v-if="knowledgeBaseUrl" :url="knowledgeBaseUrl" @requestClose="knowledgeBaseUrl = null" />

</template>

<style>
/* Markdown is injected with v-html and `prose` is unavailable (no typography plugin), so it needs its own rules. */
.agent-markdown a {
	color: #818cf8;
	font-weight: 600;
	text-decoration: underline;
	text-underline-offset: 2px;
	text-decoration-thickness: 1px;
}

.agent-markdown a:hover {
	text-decoration-thickness: 2px;
	opacity: 0.8;
}

.agent-markdown p+p {
	margin-top: 0.5rem;
}

.agent-markdown ul,
.agent-markdown ol {
	margin: 0.5rem 0;
	padding-left: 1.25rem;
	list-style: revert;
}

.agent-markdown code {
	padding: 0.1rem 0.25rem;
	border-radius: 0.25rem;
	background-color: rgb(0 0 0 / 0.1);
	font-size: 0.85em;
}

.agent-markdown pre {
	margin: 0.5rem 0;
	padding: 0.5rem;
	border-radius: 0.375rem;
	background-color: rgb(0 0 0 / 0.1);
	overflow-x: auto;
}

.agent-markdown pre code {
	padding: 0;
	background-color: transparent;
}

.agent-markdown pre code {
	padding: 0;
	background-color: transparent;
}

/* Chat message animation */
.chat-message-in {
	animation: chat-message-in 0.3s ease-out;
}

@keyframes chat-message-in {
	from {
		opacity: 0;
		transform: translateY(8px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Chat scroll styling */
.chat-scroll {
	scrollbar-width: thin;
	scrollbar-color: rgb(0 0 0 / 0.2) transparent;
}

.chat-scroll::-webkit-scrollbar {
	width: 6px;
}

.chat-scroll::-webkit-scrollbar-track {
	background: transparent;
}

.chat-scroll::-webkit-scrollbar-thumb {
	background-color: rgb(0 0 0 / 0.2);
	border-radius: 9999px;
}

.chat-scroll::-webkit-scrollbar-thumb:hover {
	background-color: rgb(0 0 0 / 0.35);
}
</style>

<script lang="ts">
import { mapStores } from "pinia";
import { api } from "@/utils/Api.util";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import { useUsersStore } from "@/stores/Users.store";
import { User } from "@/models/User.model";
import SmaSyLocalStorage from "@/utils/SmaSyLocalStorage.util";
import UserAvatar from "@/components/UserAvatar.vue";
import KBView from "@/components/KBView.vue";
import {
	XMarkIcon,
	PaperAirplaneIcon,
	ArrowPathIcon,
} from "@heroicons/vue/24/outline";
import { DateTime } from "luxon";
import { marked } from "marked";
import DOMPurify from "dompurify";

type ChatMessage = {
	role: string;
	content: string;
	at: string;
	error?: boolean;
};

// Duration after which the chat will automatically reset due to inactivity.
const CHAT_RESET_AFTER_MS = 60 * 60 * 1000; // 60 minutes

// Key used to store the chat state in localStorage.
// Conversation would be lost after every page reload, not just after 60 minutes.
const CHAT_STORAGE_KEY = "agentChatState";

// Maximum number of previous messages to retain in the chat history.
const MAX_HISTORY_MESSAGES = 5;


export default {
	name: "AgentChat",
	props: {
		open: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["update:open"],

	components: {
		UserAvatar,
		KBView,
		XMarkIcon,
		PaperAirplaneIcon,
		ArrowPathIcon,
	},

	data() {
		return {
			showChat: this.open,
			lastOpenedAt: null as number | null,
			messages: [] as ChatMessage[],
			input: "",
			loading: false,
			greeting: false,
			knowledgeBaseUrl: null as string | null,
		};
	},

	watch: {
		open(value: boolean) {
			if (value) this.openChat();
			else this.showChat = false;
		},
		showChat(value: boolean) {
			this.$emit("update:open", value);
			this.lastOpenedAt = Date.now();
			this.saveState();
		},
		messages: {
			deep: true,
			handler() {
				if (this.showChat) this.lastOpenedAt = Date.now();
				this.saveState();
			},
		},
	},

	computed: {
		...mapStores(useUsersStore),

		me(): User | null {
			return this.usersStore.me as User | null;
		},
	},

	mounted() {
		// listen for keydown events to handle chat input shortcuts, which are: Enter to send, Escape to close.
		document.addEventListener("keydown", this.onKeyDown);

		// add listener for document click events to handle clicks outside the chat component.
		document.addEventListener("click", this.onDocumentClick, true);

		// restore chat state (like: showChat, lastOpenedAt, loading, messages) from localStorage if available
		this.restoreState();
	},

	beforeUnmount() {
		// listen for keydown events to handle chat input shortcuts, which are: Enter to send, Escape to close.
		document.removeEventListener("keydown", this.onKeyDown);

		// remove listener for document click events to stop handling clicks outside the chat component.
		document.removeEventListener("click", this.onDocumentClick, true);
	},

	methods: {
		// ==========================================
		// All chat related methods
        // ==========================================
		openChat() {
			const now = Date.now();
			// set expiration for chat session based on last opened time
			const expired = this.lastOpenedAt !== null && now - this.lastOpenedAt > CHAT_RESET_AFTER_MS;

			if (expired) this.resetChat();
			this.lastOpenedAt = now; // restart the 60 minute countdown

			this.showChat = true;

			if (this.messages.length === 0) this.startGreeting();

			// always scroll to the bottom after opening the chat
			this.$nextTick(() => {
				(this.$refs.input as HTMLInputElement | undefined)?.focus();
				this.scrollToBottom();
			});
		},

		closeChat() {
			this.showChat = false;
		},

		// reset chat after an hour of inactivity
		resetChat() {
			this.messages = [];
			this.input = "";
			this.loading = false;
			this.greeting = false;
		},

		// restart chat with a fresh chat by hitting the restart button
		restartChat() {
			this.resetChat();
			this.startGreeting();
			this.$nextTick(() => (this.$refs.input as HTMLInputElement | undefined)?.focus());
		},

		// start greeting after new chat session
		startGreeting() {
			this.greeting = true;
			setTimeout(() => {
				this.greeting = false;
				this.greet();
			}, 1200);
		},

		// hardcoded greeting message (translated)
		greet() {
			this.addMessage("assistant", this.$t("Hello, I’m Tibo, and I can help you use Smart Symbols. How can I help you today?"));
		},

        // ==========================================
		// storage related methods
        // ==========================================
		// save the current chat state to local storage
		saveState() {
			const userId = this.me?.id;
			if (!userId) return;

			try {
				SmaSyLocalStorage.setUserItem(userId, CHAT_STORAGE_KEY, {
					showChat: this.showChat,
					lastOpenedAt: this.lastOpenedAt,
					loading: this.loading,
					messages: this.messages,
				});
			} catch {
				// storage unavailable or full, ignore
			}
		},

		// restore the chat state from local storage
		restoreState() {
			// Remove the old unscoped conversation from localStorage.
			localStorage.removeItem("agentChatState");

			const userId = this.me?.id;
			if (!userId) return;

			try {
				const saved = SmaSyLocalStorage.getUserItem(userId, CHAT_STORAGE_KEY);
				if (!saved || typeof saved !== "object") return;

				const expired = !saved.lastOpenedAt || Date.now() - saved.lastOpenedAt > CHAT_RESET_AFTER_MS;

				if (expired) {
					SmaSyLocalStorage.removeUserItem(userId, CHAT_STORAGE_KEY);
					return;
				}

				this.messages = saved.messages ?? [];
				this.lastOpenedAt = saved.lastOpenedAt;

				if (saved.showChat) this.openChat();

				// if the chat was in a loading state, re-request the last user message
				if (saved.loading) {
					const lastUserMessage = [...this.messages].reverse().find((m) => m.role === "user");
					if (lastUserMessage) this.requestReply(lastUserMessage.content);
				}
			} catch {
				SmaSyLocalStorage.removeUserItem(userId, CHAT_STORAGE_KEY);
			}
		},

        // ==========================================
		// messaging and communication related methods
        // ==========================================
		async sendMessage() {
			if (!this.input.trim()) return;

			const userMessage = this.input;
			this.addMessage("user", userMessage);
			this.input = "";
			await this.requestReply(userMessage);
		},

		async requestReply(userMessage: string) {
			this.loading = true;
			this.saveState(); // persist the "waiting for answer" state right away

			try {
				const { data } = await api.post("agent/chat", {
					message: userMessage,
					history: this.historyPayload(),
				});
				const reply = data.choices?.[0]?.message?.content ?? this.$t("No response");
				this.addMessage("assistant", reply);
			} catch (e: any) {
				const message = e instanceof ApiErrorMessageBag
					? e.toString()
					: this.$t("An error occurred");
				this.addMessage("assistant", message, true);
			} finally {
				this.loading = false;
				this.saveState(); // also saves the answer if it arrives after a route change
				this.$nextTick(() => (this.$refs.input as HTMLInputElement | undefined)?.focus());
			}
		},

		addMessage(role: string, content: string, error = false) {
			this.messages.push({
				role,
				content,
				at: DateTime.now().toLocaleString(DateTime.TIME_SIMPLE),
				error,
			});
			this.scrollToBottom();
		},

		// prepare the message history payload to be sent with the next user message
		historyPayload(): { role: string; content: string }[] {
			// find the index of the last user message to exclude it and subsequent messages from the history payload
			const lastQuestionIndex = this.messages.map((m) => m.role).lastIndexOf("user");
			const earlierMessages = lastQuestionIndex === -1 ? this.messages : this.messages.slice(0, lastQuestionIndex);

			return earlierMessages
				.filter((m) => !m.error) // exclude messages that encountered errors from the history payload
				.slice(-MAX_HISTORY_MESSAGES) // keep only the most recent MAX_HISTORY_MESSAGES messages in the history payload
				.map(({ role, content }) => ({ role, content })); // extract only the role and content for the history payload
		},

        // ==========================================
		// UI and content rendering related methods
        // ==========================================
		// The agent's responses are Markdown formatted and need to be rendered as HTML
		renderMarkdown(content: string) {
			const rawHtml = marked.parse(content, { breaks: true, async: false }) as string;
			const clean = DOMPurify.sanitize(rawHtml, { ADD_ATTR: ["target", "rel"] });

			const doc = new DOMParser().parseFromString(clean, "text/html");
			doc.querySelectorAll("a").forEach((link) => {
				const knowledgeBaseUrl = this.normalizeKnowledgeBaseUrl(link.getAttribute("href"));

				if (knowledgeBaseUrl) {
					link.dataset.kbUrl = knowledgeBaseUrl;
					link.removeAttribute("target");
					link.removeAttribute("rel");
				} else {
					link.setAttribute("target", "_blank");
					link.setAttribute("rel", "noopener noreferrer");
				}
			});

			return doc.body.innerHTML;
		},

		// Normalize Notion knowledge base URLs to a standard format to let it open in KBView
		normalizeKnowledgeBaseUrl(url: string | null): string | null {
			if (!url) return null;

			try {
				const parsedUrl = new URL(url);
				if (parsedUrl.hostname !== "handsomely-cicada-2cb.notion.site") return null;

				const pageId = parsedUrl.pathname.match(/([0-9a-f]{32})$/i)?.[1];
				return pageId ? `${parsedUrl.origin}/ebd/${pageId}` : null;
			} catch {
				return null;
			}
		},

		scrollToBottom() {
			this.$nextTick(() => {
				const list = this.$refs.messageList as HTMLElement | undefined;
				if (list) list.scrollTop = list.scrollHeight;
			});
		},

        // ==========================================
		// Event handlers related methods
        // ==========================================
		handleMessageLinkClick(event: MouseEvent) {
			const target = event.target as HTMLElement | null;
			const link = target?.closest("a[data-kb-url]") as HTMLAnchorElement | null;
			if (!link) return;

			event.preventDefault();
			this.knowledgeBaseUrl = link.dataset.kbUrl ?? null;
		},

		// when the user clicks in the standard layout at one of the few buttons that close the chat
		onDocumentClick(event: MouseEvent) {
			if (!this.showChat) return;

			const target = event.target as HTMLElement | null;
			if (target?.closest("[data-closes-chat]")) this.closeChat();
		},

		// happens when the user presses the Enter key in the chat input
		handleEnterKey(event: KeyboardEvent) {
			// If user presses Shift + Enter, let the browser create a newline as normal
			if (event.shiftKey) return;

			// Prevent default newline behavior and send the message
			event.preventDefault();
			this.sendMessage();
		},

		// happens when the user presses escape while the chat is open
		onKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape" && this.showChat) {
				this.closeChat();
			}
		},
	},
};
</script>
